import { useParams } from 'js/lib/useRouter';

import type { ButtonName, CheckboxName, PageTabName, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

interface EnterpriseAdminAccountTrackData {
  licenseCount?: number;
  totalAmount?: string;
}

/**
 * Custom hook that provides set of tracking functions which will include
 * the organization slug and optional contractId.
 *
 * Tracking is handled via Event Pulse.
 */
export const useTrackerWithCustomData = () => {
  const track = useTracker();
  const { thirdPartySlug } = useParams();

  /**
   * Helper function to construct the enterprise data object.
   *
   * @param contractId The optional contract ID to include in the enterprise data.
   * @returns An object containing the enterprise data.
   */
  function getEnterpriseData(contractId?: string) {
    return {
      organizationSlug: thirdPartySlug,
      ...(contractId && { contractId }),
    };
  }

  /**
   * This function tracks a button click event with the specified button name
   * and includes the organization slug and optional contract ID.
   *
   * @param buttonName The name of the button to track.
   * @param contractId The optional contract ID associated with the button click.
   */
  function trackButtonClick(
    buttonName: ButtonName,
    contractId?: string,
    options?: { clickStatus?: 'valid' | 'invalid' }
  ) {
    track('click_button', {
      button: {
        name: buttonName,
        ...(options?.clickStatus && { clickStatus: options.clickStatus }),
      },
      enterprise: getEnterpriseData(contractId),
    });
  }

  /**
   * This function tracks a page section view event with the specified section name
   * @param pageSectionName The name of the page section to track.
   * @param contractId The optional contract ID associated with the page section view.
   */
  function trackViewPageSection(
    pageSectionName: SectionName,
    contractId: string,
    enterpriseAdminAccount?: EnterpriseAdminAccountTrackData
  ) {
    track('view_page_section', {
      pageSection: {
        sectionName: pageSectionName,
      },
      enterprise: getEnterpriseData(contractId),
      enterpriseAdminAccount,
    });
  }

  /**
   * This function tracks a checkbox click event with the specified checkbox name
   * @param checkBoxName The name of the checkbox to track.
   * @param contractId The optional contract ID associated with the checkbox click.
   */
  function trackClickCheckBox(
    checkBoxName: CheckboxName,
    { checked, label }: { checked: boolean; label: string },
    contractId?: string
  ) {
    track('click_checkbox', {
      checkbox: {
        name: checkBoxName,
        checked,
        label,
      },
      enterprise: getEnterpriseData(contractId),
    });
  }

  function trackClickTextInput(inputName: string, contractId?: string) {
    track('click_text_input', {
      textInput: {
        name: inputName,
      },
      enterprise: getEnterpriseData(contractId),
    });
  }

  function trackChangeTextInput(inputName: string, contractId?: string) {
    track('change_text_input', {
      textInput: {
        name: inputName,
      },
      enterprise: getEnterpriseData(contractId),
    });
  }

  /**
   *
   * This function tracks a tab change event with the specified tab name
   * @param tabName The name of the tab to track.
   * @param contractId The optional contract ID associated with the tab change.
   */
  function trackChangeTab(tabName: PageTabName, contractId?: string) {
    track('click_page_tab', {
      pageTab: {
        name: tabName,
      },
      enterprise: getEnterpriseData(contractId),
    });
  }

  return {
    trackButtonClick,
    trackViewPageSection,
    trackChangeTab,
    trackClickCheckBox,
    trackClickTextInput,
    trackChangeTextInput,
  };
};
